import React, { useCallback, useEffect } from 'react';

import { useBreakpoints, useMedia, useWebGlLayerZIndex } from '@pkg/hooks';

import { useLockBodyScroll } from '@app/helpers';
import { useNavigationStore } from '@app/store/zustand';

import { MobileNav } from './MobileNav';
import { Modals } from './Modals';
import { SidebarNav } from './SidebarNav';
import { TopNav } from './TopNav';

import { Background, ContentColumn, ContentWrapper, Wrapper } from './styles';

interface NavigationProps {
    children: React.ReactNode;
    bgImage?: string;
    hideBackground?: boolean;
    addButton?: React.ReactNode;
    displayZIndexTop?: boolean;
    className?: string;
    hideTopNav?: boolean;
}
export const PADDING_LEFT_MIN = 118;
export const PADDING_LEFT_MAX = 360;
export const useNavPaddingLeft = (): (() => number) => {
    const { isMobile } = useBreakpoints();
    const isMinimized = useNavigationStore((state) => state.isMinimized);

    return useCallback(() => {
        if (isMobile) return 0;
        if (isMinimized) return PADDING_LEFT_MIN;
        return PADDING_LEFT_MAX;
    }, [isMinimized, isMobile]);
};

export const Navigation = ({
    children,
    bgImage,
    hideBackground,
    addButton,
    className,
    displayZIndexTop,
    hideTopNav,
}: NavigationProps) => {
    const { isMobile } = useBreakpoints();
    const { setDefaultLayerZIndex, setLayerToTop } = useWebGlLayerZIndex();
    const setIsMinimized = useNavigationStore((state) => state.setIsMinimized);
    const showMobileMenu = useNavigationStore((state) => state.showMobileMenu);

    const paddingLeft = useNavPaddingLeft();

    useLockBodyScroll(isMobile && !!showMobileMenu);

    useEffect(() => {
        if (showMobileMenu && isMobile) setLayerToTop();
        else {
            if (displayZIndexTop) setLayerToTop();
            else setDefaultLayerZIndex();
        }
    }, [showMobileMenu, isMobile, setDefaultLayerZIndex, setLayerToTop, displayZIndexTop]);

    const forceMinimizeNav = useMedia(`(max-width: 1024px)`);

    useEffect(() => {
        if (forceMinimizeNav) {
            setIsMinimized(true);
        }
    }, [forceMinimizeNav, setIsMinimized]);

    return (
        <Wrapper
            initial={false}
            animate={{ paddingLeft: paddingLeft() }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
        >
            {(!isMobile || showMobileMenu) && <SidebarNav displayZIndexTop={displayZIndexTop} />}
            {isMobile && <MobileNav />}

            <ContentColumn $hideContent={showMobileMenu && isMobile} className={className}>
                {!isMobile && !hideTopNav && (
                    <TopNav bgImage={bgImage} hideBackground={hideBackground} addButton={addButton} />
                )}
                <ContentWrapper>{children}</ContentWrapper>
            </ContentColumn>

            {!hideBackground && <Background $bgImage={bgImage} />}

            <Modals />
        </Wrapper>
    );
};
